import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage, auth } from '../firebase/config';
import { getQuarterAndYear, calculateITC, calculateOutputGST } from './gstCalculations';

/**
 * Database Service for GST Management System
 * Handles all CRUD operations for Firestore collections
 */

class FirebaseService {
  constructor() {
    this.userId = null;
    
    // Listen for auth state changes
    auth.onAuthStateChanged((user) => {
      this.userId = user?.uid || null;
    });
  }

  // Helper method to ensure user is authenticated
  ensureAuth() {
    if (!this.userId) {
      throw new Error('User must be authenticated');
    }
    return this.userId;
  }

  // SUPPLIERS CRUD OPERATIONS
  async addSupplier(supplierData) {
    const userId = this.ensureAuth();
    
    const supplier = {
      ...supplierData,
      userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'suppliers'), supplier);
    return { id: docRef.id, ...supplier };
  }

  async getSuppliers() {
    const userId = this.ensureAuth();
    
    const q = query(
      collection(db, 'suppliers'),
      where('userId', '==', userId),
      orderBy('supplierName')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  async updateSupplier(supplierId, updates) {
    const userId = this.ensureAuth();
    
    const supplierRef = doc(db, 'suppliers', supplierId);
    await updateDoc(supplierRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    
    return { id: supplierId, ...updates };
  }

  async deleteSupplier(supplierId) {
    const userId = this.ensureAuth();
    await deleteDoc(doc(db, 'suppliers', supplierId));
  }

  // PURCHASES CRUD OPERATIONS
  async addPurchase(purchaseData, invoiceFile = null) {
    const userId = this.ensureAuth();
    
    let invoiceUrl = null;
    let invoiceFileName = null;

    // Upload invoice file if provided
    if (invoiceFile) {
      const { quarter, year } = getQuarterAndYear(purchaseData.purchaseDate);
      const fileName = `${Date.now()}_${invoiceFile.name}`;
      const storageRef = ref(storage, `invoices/${userId}/purchases/${year}/${quarter}/${fileName}`);
      
      await uploadBytes(storageRef, invoiceFile);
      invoiceUrl = await getDownloadURL(storageRef);
      invoiceFileName = fileName;
    }

    // Calculate ITC
    const itcData = calculateITC(purchaseData.purchaseAmount, purchaseData.gstAmount);
    const { quarter, year } = getQuarterAndYear(purchaseData.purchaseDate);

    const purchase = {
      ...purchaseData,
      ...itcData,
      userId,
      quarter,
      year,
      invoiceUrl,
      invoiceFileName,
      purchaseDate: Timestamp.fromDate(new Date(purchaseData.purchaseDate)),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'purchases'), purchase);
    return { id: docRef.id, ...purchase };
  }

  async getPurchases(filters = {}) {
    const userId = this.ensureAuth();
    
    let q = query(
      collection(db, 'purchases'),
      where('userId', '==', userId),
      orderBy('purchaseDate', 'desc')
    );

    // Apply filters
    if (filters.quarter) {
      q = query(q, where('quarter', '==', filters.quarter));
    }
    if (filters.year) {
      q = query(q, where('year', '==', filters.year));
    }
    if (filters.supplierId) {
      q = query(q, where('supplierId', '==', filters.supplierId));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      purchaseDate: doc.data().purchaseDate?.toDate()
    }));
  }

  async updatePurchase(purchaseId, updates, newInvoiceFile = null) {
    const userId = this.ensureAuth();
    
    let invoiceUrl = updates.invoiceUrl;
    let invoiceFileName = updates.invoiceFileName;

    // Handle new invoice file upload
    if (newInvoiceFile) {
      // Delete old file if exists
      if (updates.invoiceFileName) {
        const { quarter, year } = getQuarterAndYear(updates.purchaseDate);
        const oldFileRef = ref(storage, `invoices/${userId}/purchases/${year}/${quarter}/${updates.invoiceFileName}`);
        try {
          await deleteObject(oldFileRef);
        } catch (error) {
          console.warn('Could not delete old invoice file:', error);
        }
      }

      // Upload new file
      const { quarter, year } = getQuarterAndYear(updates.purchaseDate);
      const fileName = `${Date.now()}_${newInvoiceFile.name}`;
      const storageRef = ref(storage, `invoices/${userId}/purchases/${year}/${quarter}/${fileName}`);
      
      await uploadBytes(storageRef, newInvoiceFile);
      invoiceUrl = await getDownloadURL(storageRef);
      invoiceFileName = fileName;
    }

    // Recalculate ITC if amounts changed
    const itcData = calculateITC(updates.purchaseAmount, updates.gstAmount);
    const { quarter, year } = getQuarterAndYear(updates.purchaseDate);

    const purchaseRef = doc(db, 'purchases', purchaseId);
    const updateData = {
      ...updates,
      ...itcData,
      quarter,
      year,
      invoiceUrl,
      invoiceFileName,
      purchaseDate: Timestamp.fromDate(new Date(updates.purchaseDate)),
      updatedAt: Timestamp.now()
    };

    await updateDoc(purchaseRef, updateData);
    return { id: purchaseId, ...updateData };
  }

  async deletePurchase(purchaseId) {
    const userId = this.ensureAuth();
    
    // Get purchase data to delete associated file
    const purchaseDoc = await getDoc(doc(db, 'purchases', purchaseId));
    if (purchaseDoc.exists()) {
      const purchaseData = purchaseDoc.data();
      
      // Delete associated invoice file
      if (purchaseData.invoiceFileName) {
        const fileRef = ref(storage, `invoices/${userId}/purchases/${purchaseData.year}/${purchaseData.quarter}/${purchaseData.invoiceFileName}`);
        try {
          await deleteObject(fileRef);
        } catch (error) {
          console.warn('Could not delete invoice file:', error);
        }
      }
    }

    await deleteDoc(doc(db, 'purchases', purchaseId));
  }

  // SALES CRUD OPERATIONS
  async addSale(saleData, invoiceFile = null) {
    const userId = this.ensureAuth();
    
    let invoiceUrl = null;
    let invoiceFileName = null;

    // Upload invoice file if provided
    if (invoiceFile) {
      const { quarter, year } = getQuarterAndYear(saleData.saleDate);
      const fileName = `${Date.now()}_${invoiceFile.name}`;
      const storageRef = ref(storage, `invoices/${userId}/sales/${year}/${quarter}/${fileName}`);
      
      await uploadBytes(storageRef, invoiceFile);
      invoiceUrl = await getDownloadURL(storageRef);
      invoiceFileName = fileName;
    }

    // Calculate output GST
    const gstData = calculateOutputGST(saleData.saleAmount, saleData.isGstInclusive || true);
    const { quarter, year } = getQuarterAndYear(saleData.saleDate);

    const sale = {
      ...saleData,
      ...gstData,
      userId,
      quarter,
      year,
      invoiceUrl,
      invoiceFileName,
      saleDate: Timestamp.fromDate(new Date(saleData.saleDate)),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'sales'), sale);
    return { id: docRef.id, ...sale };
  }

  async getSales(filters = {}) {
    const userId = this.ensureAuth();
    
    let q = query(
      collection(db, 'sales'),
      where('userId', '==', userId),
      orderBy('saleDate', 'desc')
    );

    // Apply filters
    if (filters.quarter) {
      q = query(q, where('quarter', '==', filters.quarter));
    }
    if (filters.year) {
      q = query(q, where('year', '==', filters.year));
    }
    if (filters.paymentMethod) {
      q = query(q, where('paymentMethod', '==', filters.paymentMethod));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      saleDate: doc.data().saleDate?.toDate()
    }));
  }

  async updateSale(saleId, updates, newInvoiceFile = null) {
    const userId = this.ensureAuth();
    
    let invoiceUrl = updates.invoiceUrl;
    let invoiceFileName = updates.invoiceFileName;

    // Handle new invoice file upload
    if (newInvoiceFile) {
      // Delete old file if exists
      if (updates.invoiceFileName) {
        const { quarter, year } = getQuarterAndYear(updates.saleDate);
        const oldFileRef = ref(storage, `invoices/${userId}/sales/${year}/${quarter}/${updates.invoiceFileName}`);
        try {
          await deleteObject(oldFileRef);
        } catch (error) {
          console.warn('Could not delete old invoice file:', error);
        }
      }

      // Upload new file
      const { quarter, year } = getQuarterAndYear(updates.saleDate);
      const fileName = `${Date.now()}_${newInvoiceFile.name}`;
      const storageRef = ref(storage, `invoices/${userId}/sales/${year}/${quarter}/${fileName}`);
      
      await uploadBytes(storageRef, newInvoiceFile);
      invoiceUrl = await getDownloadURL(storageRef);
      invoiceFileName = fileName;
    }

    // Recalculate GST if amounts changed
    const gstData = calculateOutputGST(updates.saleAmount, updates.isGstInclusive || true);
    const { quarter, year } = getQuarterAndYear(updates.saleDate);

    const saleRef = doc(db, 'sales', saleId);
    const updateData = {
      ...updates,
      ...gstData,
      quarter,
      year,
      invoiceUrl,
      invoiceFileName,
      saleDate: Timestamp.fromDate(new Date(updates.saleDate)),
      updatedAt: Timestamp.now()
    };

    await updateDoc(saleRef, updateData);
    return { id: saleId, ...updateData };
  }

  async deleteSale(saleId) {
    const userId = this.ensureAuth();
    
    // Get sale data to delete associated file
    const saleDoc = await getDoc(doc(db, 'sales', saleId));
    if (saleDoc.exists()) {
      const saleData = saleDoc.data();
      
      // Delete associated invoice file
      if (saleData.invoiceFileName) {
        const fileRef = ref(storage, `invoices/${userId}/sales/${saleData.year}/${saleData.quarter}/${saleData.invoiceFileName}`);
        try {
          await deleteObject(fileRef);
        } catch (error) {
          console.warn('Could not delete invoice file:', error);
        }
      }
    }

    await deleteDoc(doc(db, 'sales', saleId));
  }

  // GST SUMMARY OPERATIONS
  async generateQuarterlySummary(quarter, year) {
    const userId = this.ensureAuth();
    
    // Get purchases for the quarter
    const purchasesQuery = query(
      collection(db, 'purchases'),
      where('userId', '==', userId),
      where('quarter', '==', quarter),
      where('year', '==', year)
    );
    
    const salesQuery = query(
      collection(db, 'sales'),
      where('userId', '==', userId),
      where('quarter', '==', quarter),
      where('year', '==', year)
    );

    const [purchasesSnapshot, salesSnapshot] = await Promise.all([
      getDocs(purchasesQuery),
      getDocs(salesQuery)
    ]);

    const purchases = purchasesSnapshot.docs.map(doc => doc.data());
    const sales = salesSnapshot.docs.map(doc => doc.data());

    // Calculate summary using the calculation service
    const { calculateQuarterlySummary } = await import('./gstCalculations');
    const summary = calculateQuarterlySummary(purchases, sales);

    // Save summary to database
    const summaryData = {
      userId,
      quarter,
      year,
      ...summary,
      generatedAt: Timestamp.now()
    };

    const summaryRef = doc(db, 'gst_summaries', `${userId}_${year}_${quarter}`);
    await updateDoc(summaryRef, summaryData);

    return summaryData;
  }

  async getQuarterlySummary(quarter, year) {
    const userId = this.ensureAuth();
    
    const summaryRef = doc(db, 'gst_summaries', `${userId}_${year}_${quarter}`);
    const summaryDoc = await getDoc(summaryRef);
    
    if (summaryDoc.exists()) {
      return summaryDoc.data();
    }
    
    // Generate summary if it doesn't exist
    return await this.generateQuarterlySummary(quarter, year);
  }

  // USER PROFILE OPERATIONS
  async saveUserProfile(profileData) {
    const userId = this.ensureAuth();
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...profileData,
      updatedAt: Timestamp.now()
    });
    
    return profileData;
  }

  async getUserProfile() {
    const userId = this.ensureAuth();
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    
    return null;
  }
}

// Create singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;
